// src/components/ContactForm.tsx
import { useState, useRef, FormEvent } from 'react';
import { Shield } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAnalytics } from '../hooks/useAnalytics';

interface FormState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  message: string;
}

interface SubmissionData {
  name: string;
  email: string;
  company: string;
  token: string;
}

const ContactForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [formState, setFormState] = useState<FormState>({ 
    status: 'idle', 
    message: '' 
  });
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { trackEvent } = useAnalytics();

  const resetForm = () => {
    setName('');
    setEmail('');
    setCompany('');
    recaptchaRef.current?.reset();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState({ status: 'submitting', message: '' });

    try {
      const token = recaptchaRef.current?.getValue();
      if (!token) {
        trackEvent('form_validation_error', {
          form_type: 'waitlist',
          error_type: 'recaptcha_missing'
        });
        throw new Error('Please complete the captcha verification');
      }

      const data: SubmissionData = {
        name,
        email,
        company,
        token
      };

      trackEvent('form_submit_attempt', {
        form_type: 'waitlist'
      });

      const response = await fetch(import.meta.env.VITE_CONTACT_FORM_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        trackEvent('form_submission_error', {
          form_type: 'waitlist',
          error_type: errorData.message || 'api_error'
        });
        throw new Error(errorData.message || 'Failed to submit form');
      }

      trackEvent('form_submission_success', {
        form_type: 'waitlist'
      });

      setFormState({ 
        status: 'success', 
        message: 'Thanks for your interest! We\'ll be in touch soon.' 
      });
      resetForm();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      setFormState({ 
        status: 'error', 
        message: errorMessage 
      });
      console.error('Form submission error:', error);
    }
  };

  const isSubmitting = formState.status === 'submitting';
  const showSuccess = formState.status === 'success';
  const showError = formState.status === 'error';

  return (
    <section id="contact-form" className="container mx-auto px-6 py-16 bg-gradient-to-b from-blue-50 to-indigo-50">
      <div className="mx-auto max-w-2xl rounded-lg bg-white p-8 text-center shadow-xl">
        <h2 className="mb-6 text-2xl font-bold text-gray-900">Be the first to try SignoBox!</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Your Name"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <input
            type="email"
            placeholder="Your Email"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <input
            type="text"
            placeholder="Company Name"
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-900"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <div className="flex justify-center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            />
          </div>
          {showError && (
            <p className="text-red-500">{formState.message}</p>
          )}
          {showSuccess && (
            <p className="text-green-500">{formState.message}</p>
          )}
          <button
            type="submit"
            className="w-full rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Notify Me'}
          </button>
        </form>
        <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-600">
          <Shield className="h-4 w-4" />
          <p>We respect your privacy. No spam, just updates!</p>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
